import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { ChapterTransition, CHAPTER_VIDEOS } from './ChapterTransition';
import { CinematicBackground, VanGoghCanvas, AlphaThetaSpiral } from './CinematicBackground';
import { 
  SparkleIcon, HeartIcon, StarIcon, ArrowIcon, CheckIcon,
  NeedIcon, GoldIcon, SaveIcon, SendIcon, BookIcon, ProfileIcon,
  RefreshIcon, InsightIcon, CloseIcon, PlayIcon
} from './BespokeIcons';
import { GoldenSummary, GoldenSummaryButton } from './GoldenSummary';
import SoulScoreWidget from './SoulScoreWidget';

/**
 * VisionQuest - Chapter 2: The Vision
 * 
 * Following Beyond Entrepreneurship 2.0 vision development process
 * blended with Business Model Generation wisdom.
 * 
 * Three pillars:
 * 1. Core Values (The Green) - Spiritual elemental values
 * 2. Purpose - Why are you here? Reason to exist
 * 3. BHAG - Big Hairy Audacious Goal / Mission
 */

// Core values from NVC needs (The Green)
const GREEN_CORE_VALUES = [
  // Connection
  { value: 'Belonging', category: 'Connection', description: 'To be part of a community' },
  { value: 'Love', category: 'Connection', description: 'Deep affection and care' },
  { value: 'Intimacy', category: 'Connection', description: 'Close, familiar connection' },
  { value: 'Trust', category: 'Connection', description: 'Faith in reliability' },
  { value: 'Empathy', category: 'Connection', description: 'Understanding others feelings' },
  { value: 'Acceptance', category: 'Connection', description: 'Being received as you are' },
  { value: 'Companionship', category: 'Connection', description: 'Shared journey together' },
  { value: 'Community', category: 'Connection', description: 'Collective togetherness' },
  
  // Meaning
  { value: 'Purpose', category: 'Meaning', description: 'Reason for existence' },
  { value: 'Contribution', category: 'Meaning', description: 'Making a difference' },
  { value: 'Growth', category: 'Meaning', description: 'Continuous evolution' },
  { value: 'Learning', category: 'Meaning', description: 'Expanding understanding' },
  { value: 'Creativity', category: 'Meaning', description: 'Bringing new into being' },
  { value: 'Expression', category: 'Meaning', description: 'Authentic self-sharing' },
  { value: 'Legacy', category: 'Meaning', description: 'What you leave behind' },
  
  // Autonomy
  { value: 'Freedom', category: 'Autonomy', description: 'Liberty to choose' },
  { value: 'Independence', category: 'Autonomy', description: 'Self-sufficiency' },
  { value: 'Choice', category: 'Autonomy', description: 'Power of decision' },
  { value: 'Authenticity', category: 'Autonomy', description: 'Being true to self' },
  { value: 'Integrity', category: 'Autonomy', description: 'Alignment of values and action' },
  { value: 'Self-expression', category: 'Autonomy', description: 'Sharing your truth' },
  
  // Peace
  { value: 'Harmony', category: 'Peace', description: 'Balance and flow' },
  { value: 'Peace', category: 'Peace', description: 'Inner and outer tranquility' },
  { value: 'Beauty', category: 'Peace', description: 'Appreciation of aesthetics' },
  { value: 'Order', category: 'Peace', description: 'Structure and clarity' },
  { value: 'Ease', category: 'Peace', description: 'Effortless flow' },
  { value: 'Rest', category: 'Peace', description: 'Restoration and renewal' },
  
  // Physical
  { value: 'Safety', category: 'Physical', description: 'Security and protection' },
  { value: 'Health', category: 'Physical', description: 'Wellbeing of body' },
  { value: 'Vitality', category: 'Physical', description: 'Life energy and vigor' },
  { value: 'Sustenance', category: 'Physical', description: 'Nourishment' },
  { value: 'Movement', category: 'Physical', description: 'Physical expression' },
  
  // Play
  { value: 'Joy', category: 'Play', description: 'Deep happiness' },
  { value: 'Fun', category: 'Play', description: 'Lighthearted pleasure' },
  { value: 'Adventure', category: 'Play', description: 'Exciting exploration' },
  { value: 'Humor', category: 'Play', description: 'Lightness and laughter' },
  { value: 'Celebration', category: 'Play', description: 'Honoring achievements' },
];

// Group values by category
const VALUE_CATEGORIES = GREEN_CORE_VALUES.reduce((acc, item) => {
  if (!acc[item.category]) acc[item.category] = [];
  acc[item.category].push(item);
  return acc;
}, {});

// Vision Quest phases
const PHASES = {
  chapter_intro: 'chapter_intro',
  values: 'values',
  purpose: 'purpose',
  bhag: 'bhag',
  review: 'review',
  creation: 'creation',
  story: 'story',
  save: 'save',
};

// Value selection card
const ValueCard = ({ value, isSelected, onToggle }) => (
  <button
    onClick={() => onToggle(value.value)}
    className={`
      p-3 rounded-xl border-2 text-left transition-all duration-300
      ${isSelected 
        ? 'bg-emerald-500/20 border-emerald-500/50 scale-[1.02]' 
        : 'bg-zinc-800/30 border-zinc-700/30 hover:border-zinc-600/50'
      }
    `}
    data-testid={`value-card-${value.value.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div>
        <h4 className={`font-medium ${isSelected ? 'text-emerald-400' : 'text-white'}`}>
          {value.value}
        </h4>
        <p className="text-xs text-zinc-500 mt-0.5">{value.description}</p>
      </div>
      {isSelected && (
        <CheckIcon size={16} color="#10B981" className="flex-shrink-0" />
      )}
    </div>
  </button>
);

// Main Vision Quest Component
const VisionQuest = ({ 
  userId,
  userName,
  onComplete,
  onClose,
  initialData = null,
  participants = [], // For group visions
  isGroup = false
}) => {
  const navigate = useNavigate();
  
  // Phase management
  const [phase, setPhase] = useState(PHASES.chapter_intro);
  const [showChapterTransition, setShowChapterTransition] = useState(true);
  
  // Vision data
  const [selectedValues, setSelectedValues] = useState(initialData?.values || []);
  const [customValues, setCustomValues] = useState(initialData?.customValues || '');
  const [purpose, setPurpose] = useState(initialData?.purpose || '');
  const [bhag, setBhag] = useState(initialData?.bhag || '');
  const [visionTitle, setVisionTitle] = useState(initialData?.title || '');
  
  // Story generation
  const [generatedStory, setGeneratedStory] = useState('');
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  
  // Save options
  const [requestPublishing, setRequestPublishing] = useState(false);
  const [createSketchProject, setCreateSketchProject] = useState(true);
  const [requestZine, setRequestZine] = useState(false);
  const [showZineDialog, setShowZineDialog] = useState(false);
  const [zineAddress, setZineAddress] = useState({ name: '', street: '', city: '', state: '', zip: '', country: '' });
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [showGoldenSummary, setShowGoldenSummary] = useState(false);
  
  // Soul Score states
  const [showSoulScore, setShowSoulScore] = useState(false);
  const [soulScoreCheckpoint, setSoulScoreCheckpoint] = useState('vision');
  const [sessionId] = useState(() => `visionquest-${Date.now()}`);
  const [soulScoreCompleted, setSoulScoreCompleted] = useState({
    vision: false,
    story: false
  });
  
  // Background mode for current phase
  const getBackgroundConfig = () => {
    switch(phase) {
      case PHASES.values:
        return { mode: 'vangogh', palette: 'sunflowers' };
      case PHASES.purpose:
        return { mode: 'spiral' };
      case PHASES.bhag:
        return { mode: 'vangogh', palette: 'starryNight' };
      case PHASES.story:
      case PHASES.creation:
        return { mode: 'random', videoUrl: CHAPTER_VIDEOS.video2 };
      default:
        return { mode: 'vangogh', palette: 'cafeNight' };
    }
  };
  
  // Value toggle
  const toggleValue = useCallback((value) => {
    setSelectedValues(prev => 
      prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  }, []);
  
  // Handle chapter intro completion
  const handleChapterIntroComplete = () => {
    setShowChapterTransition(false);
    setPhase(PHASES.values);
  };
  
  // Generate AI story
  const generateStory = async () => {
    // Prevent double submission
    if (isGeneratingStory) {
      console.log('[Story] Already generating, ignoring duplicate request');
      return;
    }
    
    setIsGeneratingStory(true);
    setPhase(PHASES.creation);
    
    try {
      const allValues = [...selectedValues];
      if (customValues.trim()) {
        allValues.push(...customValues.split(',').map(v => v.trim()).filter(Boolean));
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-quest/generate-story`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          values: allValues,
          purpose,
          bhag,
          title: visionTitle,
          participants: isGroup ? participants.map(p => ({ id: p.id, name: p.name })) : [{ name: userName || 'Visionary' }],
          isGroup,
          // Include OFNR context if available
          ofnrContext: initialData?.ofnrContext || null
        })
      });
      
      // Clone response to enable multiple reads if needed
      const clonedResponse = response.clone();
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedStory(data.story);
        setPhase(PHASES.story);
        toast.success('Your story has been crafted!');
        
        // Show Soul Score after story generation (story checkpoint)
        if (!soulScoreCompleted.story) {
          setTimeout(() => {
            setSoulScoreCheckpoint('story');
            setShowSoulScore(true);
          }, 2000); // Give user time to see the story first
        }
      } else {
        // Try to get error details
        let errorMessage = 'Unknown error';
        try {
          const errorData = await clonedResponse.json();
          errorMessage = errorData.error || errorData.detail || response.statusText;
        } catch {
          errorMessage = response.statusText || `Server error (${response.status})`;
        }
        
        console.error('[Story] Generation failed:', response.status, errorMessage);
        
        // User-friendly error messages
        if (response.status === 500) {
          toast.error('Story generation service is temporarily unavailable. Please try again in a moment.');
        } else if (response.status === 429) {
          toast.error('Too many requests. Please wait a moment and try again.');
        } else if (response.status === 401 || response.status === 403) {
          toast.error('Authentication required. Please refresh and sign in again.');
        } else {
          toast.error(`Unable to generate story: ${errorMessage}. Please try again.`);
        }
        
        setPhase(PHASES.review);
      }
    } catch (error) {
      console.error('[Story] Network/fetch error:', error);
      
      // More helpful error messages for common issues
      if (error.message.includes('disturbed or locked')) {
        toast.error('Connection interrupted. Please try again.');
        // Reset and retry opportunity
        setPhase(PHASES.review);
      } else if (error.message.includes('Failed to fetch')) {
        toast.error('Cannot reach the server. Please check your internet connection and try again.');
        setPhase(PHASES.review);
      } else if (error.name === 'AbortError') {
        toast.error('Request timed out. Please try again.');
        setPhase(PHASES.review);
      } else {
        toast.error(`Something went wrong: ${error.message}. Please try again.`);
        setPhase(PHASES.review);
      }
    } finally {
      setIsGeneratingStory(false);
    }
  };
  
  // Save vision quest
  const saveVision = async () => {
    // Check if user is logged in
    const effectiveUserId = userId || localStorage.getItem('elchme_user_id') || `anonymous-${Date.now()}`;
    const effectiveUserName = userName || localStorage.getItem('elchme_user_name') || 'Anonymous Visionary';
    
    if (!effectiveUserId || effectiveUserId.startsWith('anonymous')) {
      toast.warning('Sign in to save your Vision Quest permanently. Saving as guest...', { duration: 4000 });
    }
    
    setIsSaving(true);
    
    try {
      const allValues = [...selectedValues];
      if (customValues.trim()) {
        allValues.push(...customValues.split(',').map(v => v.trim()).filter(Boolean));
      }
      
      const visionData = {
        userId: effectiveUserId,
        title: visionTitle || 'My Vision Quest',
        values: allValues,
        purpose,
        bhag,
        story: generatedStory,
        isGroup,
        participants: isGroup ? participants : [{ id: effectiveUserId, name: effectiveUserName }],
        requestPublishing,
        createSketchProject,
        zineRequest: requestZine ? zineAddress : null,
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-quest/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(visionData)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Vision Quest saved to your LORE library!');
        
        if (requestPublishing) {
          toast.info('Publishing request submitted for review.');
        }
        
        if (createSketchProject) {
          toast.success('SketchHub project created!');
        }
        
        if (requestZine) {
          toast.success('Zine request submitted! Check your mail soon.');
        }
        
        onComplete?.(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save failed:', errorData);
        toast.error(errorData.detail || 'Failed to save vision. Please try again.');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Connection error. Please check your internet and try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Navigation
  const goNext = () => {
    switch(phase) {
      case PHASES.values:
        setPhase(PHASES.purpose);
        break;
      case PHASES.purpose:
        setPhase(PHASES.bhag);
        break;
      case PHASES.bhag:
        // Show Soul Score checkpoint after completing vision phases
        if (!soulScoreCompleted.vision) {
          setSoulScoreCheckpoint('vision');
          setShowSoulScore(true);
          // The phase transition will happen in handleSoulScoreComplete
        } else {
          setPhase(PHASES.review);
        }
        break;
      case PHASES.review:
        setShowChapterTransition(true);
        // Show Creation chapter, then generate story
        setTimeout(() => {
          setShowChapterTransition(false);
          generateStory();
        }, 6000);
        break;
      case PHASES.story:
        setPhase(PHASES.save);
        break;
      default:
        break;
    }
  };
  
  const goBack = () => {
    switch(phase) {
      case PHASES.purpose:
        setPhase(PHASES.values);
        break;
      case PHASES.bhag:
        setPhase(PHASES.purpose);
        break;
      case PHASES.review:
        setPhase(PHASES.bhag);
        break;
      case PHASES.story:
        setPhase(PHASES.review);
        break;
      case PHASES.save:
        setPhase(PHASES.story);
        break;
      default:
        break;
    }
  };
  
  // Chapter intro screen
  if (showChapterTransition && phase === PHASES.chapter_intro) {
    return (
      <ChapterTransition 
        chapter="vision"
        onComplete={handleChapterIntroComplete}
        autoAdvance={true}
        showSkip={true}
      />
    );
  }
  
  // Creation chapter before story (shown briefly during review → story transition)
  if (showChapterTransition && phase === PHASES.review) {
    return (
      <ChapterTransition 
        chapter="creation"
        onComplete={() => {}}
        autoAdvance={false}
        showSkip={false}
      />
    );
  }
  
  const bgConfig = getBackgroundConfig();
  
  return (
    <div className="fixed inset-0 z-40 overflow-hidden" data-testid="vision-quest">
      {/* Cinematic Background */}
      <CinematicBackground 
        mode={bgConfig.mode}
        palette={bgConfig.palette}
        videoUrl={bgConfig.videoUrl}
        speed={0.15}
      />
      
      {/* Content Layer */}
      <div className="relative z-10 h-full overflow-y-auto">
        <div className="min-h-full flex flex-col py-8 px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                <GoldIcon size={20} color="#FBBF24" />
              </div>
              <div>
                <h2 className="text-white text-lg font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Vision Quest
                </h2>
                <p className="text-white/50 text-xs">Chapter 2</p>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              className="text-white/50 hover:text-white"
            >
              <CloseIcon size={20} color="currentColor" />
            </Button>
          </div>
          
          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-8 max-w-3xl mx-auto w-full">
            {['values', 'purpose', 'bhag', 'review', 'story', 'save'].map((p, i) => (
              <div key={p} className="flex-1 flex items-center">
                <div className={`
                  h-1 flex-1 rounded-full transition-all duration-500
                  ${['values', 'purpose', 'bhag', 'review', 'story', 'save'].indexOf(phase) >= i 
                    ? 'bg-amber-500/80' 
                    : 'bg-white/10'
                  }
                `} />
              </div>
            ))}
          </div>
          
          {/* Main Content Area */}
          <div className="flex-1 max-w-4xl mx-auto w-full">
            
            {/* PHASE: Core Values */}
            {phase === PHASES.values && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    The Green: Your Core Values
                  </h1>
                  <p className="text-white/60 max-w-xl mx-auto">
                    What matters most to you? These are your spiritual, elemental values — 
                    the foundation upon which your vision will be built.
                  </p>
                </div>
                
                {/* Value Categories */}
                {Object.entries(VALUE_CATEGORIES).map(([category, values]) => (
                  <div key={category} className="mb-6">
                    <h3 className="text-emerald-400 text-sm font-medium mb-3 tracking-wider uppercase">
                      {category}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {values.map((value) => (
                        <ValueCard
                          key={value.value}
                          value={value}
                          isSelected={selectedValues.includes(value.value)}
                          onToggle={toggleValue}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Custom Values */}
                <div className="p-4 bg-zinc-800/30 rounded-xl border border-zinc-700/30">
                  <h3 className="text-white/80 text-sm font-medium mb-2">
                    Add Your Own Values
                  </h3>
                  <Input
                    value={customValues}
                    onChange={(e) => setCustomValues(e.target.value)}
                    placeholder="Enter additional values, separated by commas..."
                    className="bg-zinc-900/50 border-zinc-700/50 text-white"
                  />
                </div>
                
                {/* Selected count */}
                <div className="text-center text-white/50 text-sm">
                  {selectedValues.length} values selected
                </div>
              </div>
            )}
            
            {/* PHASE: Purpose */}
            {phase === PHASES.purpose && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Your Purpose
                  </h1>
                  <p className="text-white/60 max-w-xl mx-auto">
                    Why are you here? What is your reason to exist? 
                    This goes beyond goals — it's about the meaning you bring to life.
                  </p>
                </div>
                
                <Card className="bg-zinc-800/30 border-zinc-700/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <HeartIcon size={24} color="#F472B6" />
                      <h3 className="text-white font-medium">Your Reason to Exist</h3>
                    </div>
                    
                    <Textarea
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      placeholder="I exist to..."
                      className="bg-zinc-900/50 border-zinc-700/50 text-white min-h-[150px] placeholder:text-zinc-600"
                    />
                    
                    <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <p className="text-amber-300/80 text-sm italic">
                        "Purpose is not about what you do, but about why you do it. 
                        It's the thread that connects all your actions to something greater."
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Prompts */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <button 
                    onClick={() => setPurpose(prev => prev + (prev ? '\n' : '') + "To help others discover their authentic selves")}
                    className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <InsightIcon size={16} color="#FBBF24" /> "To help others discover..."
                  </button>
                  <button 
                    onClick={() => setPurpose(prev => prev + (prev ? '\n' : '') + "To create beauty and meaning in everything I touch")}
                    className="p-3 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <SparkleIcon size={16} color="#F472B6" /> "To create beauty and meaning..."
                  </button>
                </div>
              </div>
            )}
            
            {/* PHASE: BHAG */}
            {phase === PHASES.bhag && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Your Big Hairy Audacious Goal
                  </h1>
                  <p className="text-white/60 max-w-xl mx-auto">
                    What is your mission? Dream impossibly big. 
                    A BHAG should take 10-30 years and seem almost crazy.
                  </p>
                </div>
                
                <Card className="bg-zinc-800/30 border-zinc-700/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <StarIcon size={24} color="#FBBF24" />
                      <h3 className="text-white font-medium">Your Mission</h3>
                    </div>
                    
                    <Textarea
                      value={bhag}
                      onChange={(e) => setBhag(e.target.value)}
                      placeholder="My mission is to..."
                      className="bg-zinc-900/50 border-zinc-700/50 text-white min-h-[150px] placeholder:text-zinc-600"
                    />
                    
                    <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <p className="text-blue-300/80 text-sm italic">
                        "A true BHAG is clear and compelling, serves as unifying focal point of effort, 
                        and acts as a clear catalyst for team spirit."
                        <span className="block mt-1 text-blue-400/60">— Jim Collins, Beyond Entrepreneurship 2.0</span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Vision Title */}
                <Card className="bg-zinc-800/30 border-zinc-700/30">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <BookIcon size={20} color="#A78BFA" />
                      <h3 className="text-white font-medium">Name Your Vision</h3>
                    </div>
                    <Input
                      value={visionTitle}
                      onChange={(e) => setVisionTitle(e.target.value)}
                      placeholder="Give your vision a memorable title..."
                      className="bg-zinc-900/50 border-zinc-700/50 text-white"
                    />
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* PHASE: Review */}
            {phase === PHASES.review && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Your Vision at a Glance
                  </h1>
                  <p className="text-white/60 max-w-xl mx-auto">
                    Review your vision before we transform it into a story.
                  </p>
                </div>
                
                {/* Summary Cards */}
                <div className="space-y-4">
                  {/* Values */}
                  <Card className="bg-emerald-500/10 border-emerald-500/30">
                    <CardContent className="p-5">
                      <h3 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
                        <NeedIcon size={18} color="#10B981" />
                        Core Values
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedValues.map(v => (
                          <Badge key={v} variant="outline" className="border-emerald-500/50 text-emerald-300">
                            {v}
                          </Badge>
                        ))}
                        {customValues.split(',').filter(v => v.trim()).map(v => (
                          <Badge key={v} variant="outline" className="border-emerald-500/50 text-emerald-300">
                            {v.trim()}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Purpose */}
                  <Card className="bg-pink-500/10 border-pink-500/30">
                    <CardContent className="p-5">
                      <h3 className="text-pink-400 font-medium mb-3 flex items-center gap-2">
                        <HeartIcon size={18} color="#F472B6" />
                        Purpose
                      </h3>
                      <p className="text-white/80">{purpose || 'Not specified'}</p>
                    </CardContent>
                  </Card>
                  
                  {/* BHAG */}
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-5">
                      <h3 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                        <StarIcon size={18} color="#FBBF24" />
                        BHAG / Mission
                      </h3>
                      <p className="text-white/80">{bhag || 'Not specified'}</p>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                  <SparkleIcon size={32} color="#FBBF24" className="mx-auto mb-3" />
                  <p className="text-white/70 mb-2">
                    Click "Generate My Story" to have AI craft a fictionalized screenplay treatment of your vision quest.
                  </p>
                  <p className="text-amber-400/60 text-sm">
                    These stories can be used to teach others, and you will have the chance to add to the LORE library.
                  </p>
                </div>
              </div>
            )}
            
            {/* PHASE: Story */}
            {phase === PHASES.story && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <p className="text-amber-400/60 text-sm tracking-[0.3em] uppercase mb-2">THE CREATION</p>
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {visionTitle || 'Your Story'}
                  </h1>
                </div>
                
                {isGeneratingStory ? (
                  <div className="text-center py-20">
                    <RefreshIcon size={48} color="#FBBF24" className="mx-auto mb-4 animate-spin" />
                    <p className="text-white/60">Crafting your story...</p>
                  </div>
                ) : (
                  <>
                    <Card className="bg-zinc-900/50 border-amber-500/20 backdrop-blur-sm">
                      <CardContent className="p-8">
                        <div className="prose prose-invert max-w-none">
                          <p className="text-white/90 text-lg leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'Playfair Display', serif" }}>
                            {generatedStory}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Golden Summary Button - Primary CTA */}
                    <div className="text-center py-4">
                      <Button
                        onClick={saveVision}
                        disabled={isSaving}
                        className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25 transition-all duration-300 hover:scale-105"
                        data-testid="golden-summary-save-btn"
                      >
                        {isSaving ? (
                          <>
                            <RefreshIcon size={20} className="mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <StarIcon size={20} color="currentColor" className="mr-2" />
                            Save Golden Summary to Dashboard
                          </>
                        )}
                      </Button>
                      <p className="text-amber-400/60 text-sm mt-2">
                        Save your vision, values, purpose, BHAG, and story to your BelongingSessions dashboard
                      </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                      <Button
                        variant="ghost"
                        onClick={generateStory}
                        disabled={isGeneratingStory}
                        className="text-amber-400/70 hover:text-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RefreshIcon size={16} color="currentColor" className={`mr-2 ${isGeneratingStory ? 'animate-spin' : ''}`} />
                        {isGeneratingStory ? 'Crafting...' : 'Regenerate Story'}
                      </Button>
                      
                      <Button
                        onClick={() => navigate('/elchme')}
                        variant="outline"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <PlayIcon size={16} color="#10B981" className="mr-2" />
                        Start New ElchmE
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* PHASE: Creation (Loading with compelling animation) */}
            {phase === PHASES.creation && (
              <div className="space-y-8 animate-fadeIn">
                <div className="text-center">
                  <p className="text-amber-400/60 text-sm tracking-[0.3em] uppercase mb-4">WEAVING YOUR VISION</p>
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Creating Your Story
                  </h1>
                </div>
                
                {/* Compelling loading animation */}
                <div className="relative flex items-center justify-center py-20">
                  {/* Outer rotating ring */}
                  <div className="absolute w-40 h-40 rounded-full border-2 border-amber-500/20 animate-spin" style={{ animationDuration: '3s' }} />
                  
                  {/* Middle pulsing ring */}
                  <div className="absolute w-32 h-32 rounded-full border-2 border-amber-500/30" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
                  
                  {/* Inner spinning icon */}
                  <div className="relative">
                    <RefreshIcon size={56} color="#FBBF24" className="animate-spin" style={{ animationDuration: '2s' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <SparkleIcon size={24} color="#FCD34D" className="animate-pulse" />
                    </div>
                  </div>
                  
                  {/* Orbiting particles */}
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full bg-amber-400"
                      style={{
                        animation: `orbit 4s linear infinite`,
                        animationDelay: `${i * 0.6}s`,
                        transformOrigin: '0 0',
                        left: '50%',
                        top: '50%'
                      }}
                    />
                  ))}
                </div>
                
                <div className="max-w-md mx-auto space-y-4 text-center">
                  <p className="text-white/70 text-base leading-relaxed">
                    Transforming your values, purpose, and dreams into a narrative...
                  </p>
                  <p className="text-amber-400/60 text-sm italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                    "Every vision begins as a story in the heart"
                  </p>
                </div>
                
                <style>{`
                  @keyframes orbit {
                    0% { transform: rotate(0deg) translateX(80px) rotate(0deg); }
                    100% { transform: rotate(360deg) translateX(80px) rotate(-360deg); }
                  }
                `}</style>
              </div>
            )}
            
            {/* PHASE: Save */}
            {phase === PHASES.save && (
              <div className="space-y-6 animate-fadeIn">
                <div className="text-center mb-8">
                  <h1 className="text-3xl sm:text-4xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Complete Your Quest
                  </h1>
                  <p className="text-white/60 max-w-xl mx-auto">
                    Choose how to preserve and share your vision.
                  </p>
                </div>
                
                <div className="space-y-4 max-w-xl mx-auto">
                  {/* Save Options */}
                  <Card className="bg-zinc-800/30 border-zinc-700/30">
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="lore" 
                          checked={true} 
                          disabled 
                          className="mt-1"
                        />
                        <div>
                          <Label htmlFor="lore" className="text-white cursor-pointer">
                            Save to LORE Library
                          </Label>
                          <p className="text-white/50 text-xs">Always saved to your personal library</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="sketch" 
                          checked={createSketchProject}
                          onCheckedChange={setCreateSketchProject}
                          className="mt-1"
                        />
                        <div>
                          <Label htmlFor="sketch" className="text-white cursor-pointer">
                            Create SketchHub Project
                          </Label>
                          <p className="text-white/50 text-xs">Add to your project canvas on belongingsessions.com</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Checkbox 
                          id="publish" 
                          checked={requestPublishing}
                          onCheckedChange={setRequestPublishing}
                          className="mt-1"
                        />
                        <div>
                          <Label htmlFor="publish" className="text-white cursor-pointer">
                            Request Community Publishing
                          </Label>
                          <p className="text-white/50 text-xs">Share your fictionalized story to inspire others (admin review)</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Golden Summary - View complete journey */}
                  <div className="mt-2">
                    <GoldenSummaryButton
                      onClick={() => setShowGoldenSummary(true)}
                      label="View Complete Journey Summary"
                    />
                    <p className="text-center text-white/40 text-xs mt-2">
                      Review your values, purpose, and vision before saving
                    </p>
                  </div>
                  
                  {/* Zine Request - Wes Anderson Style */}
                  <Card 
                    className="bg-gradient-to-br from-rose-900/30 to-amber-900/30 border-rose-500/30 cursor-pointer hover:border-rose-500/50 transition-colors"
                    onClick={() => setShowZineDialog(true)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-20 bg-[#F5F0E1] rounded border-2 border-amber-800/50 flex items-center justify-center transform -rotate-3 shadow-lg">
                            <span className="text-amber-900 text-xs font-mono transform rotate-3">ZINE</span>
                          </div>
                          <div>
                            <h3 className="text-rose-300 font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                              Request a Handmade Zine
                            </h3>
                            <p className="text-white/50 text-xs mt-1">
                              A partisan, subversive pamphlet delivered via post
                            </p>
                            <p className="text-amber-400/60 text-[10px] mt-1 italic">
                              "Some things are best held in your hands"
                            </p>
                          </div>
                        </div>
                        <ArrowIcon size={20} color="#FDA4AF" direction="right" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation Footer */}
          {phase !== PHASES.chapter_intro && (
            <div className="mt-8 flex items-center justify-between max-w-4xl mx-auto w-full">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={phase === PHASES.values}
                className="text-white/60 hover:text-white"
              >
                <ArrowIcon size={16} color="currentColor" direction="left" className="mr-2" />
                Back
              </Button>
              
              {phase === PHASES.save ? (
                <Button
                  onClick={saveVision}
                  disabled={isSaving}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
                  data-testid="save-vision-btn"
                >
                  {isSaving ? (
                    <RefreshIcon size={16} color="currentColor" className="mr-2 animate-spin" />
                  ) : (
                    <SaveIcon size={16} color="currentColor" className="mr-2" />
                  )}
                  Complete Quest
                </Button>
              ) : phase === PHASES.review ? (
                <Button
                  onClick={goNext}
                  disabled={isGeneratingStory}
                  className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-medium"
                  data-testid="generate-story-btn"
                >
                  <SparkleIcon size={16} color="currentColor" className="mr-2" />
                  Generate My Story
                </Button>
              ) : (
                <Button
                  onClick={goNext}
                  className="bg-white/10 hover:bg-white/20 text-white"
                >
                  Continue
                  <ArrowIcon size={16} color="currentColor" direction="right" className="ml-2" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Zine Request Dialog */}
      <Dialog open={showZineDialog} onOpenChange={setShowZineDialog}>
        <DialogContent className="bg-[#F5F0E1] border-amber-800/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-900 font-serif text-2xl">
              📬 Request Your Zine
            </DialogTitle>
            <DialogDescription className="text-amber-800/70">
              A handcrafted, limited-edition pamphlet featuring your vision story. 
              Made with care, delivered with love.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-amber-900">Name</Label>
              <Input 
                value={zineAddress.name}
                onChange={(e) => setZineAddress(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your name"
                className="bg-white border-amber-300"
              />
            </div>
            <div>
              <Label className="text-amber-900">Street Address</Label>
              <Input 
                value={zineAddress.street}
                onChange={(e) => setZineAddress(prev => ({ ...prev, street: e.target.value }))}
                placeholder="123 Main St"
                className="bg-white border-amber-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-amber-900">City</Label>
                <Input 
                  value={zineAddress.city}
                  onChange={(e) => setZineAddress(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-white border-amber-300"
                />
              </div>
              <div>
                <Label className="text-amber-900">State</Label>
                <Input 
                  value={zineAddress.state}
                  onChange={(e) => setZineAddress(prev => ({ ...prev, state: e.target.value }))}
                  className="bg-white border-amber-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-amber-900">ZIP</Label>
                <Input 
                  value={zineAddress.zip}
                  onChange={(e) => setZineAddress(prev => ({ ...prev, zip: e.target.value }))}
                  className="bg-white border-amber-300"
                />
              </div>
              <div>
                <Label className="text-amber-900">Country</Label>
                <Input 
                  value={zineAddress.country}
                  onChange={(e) => setZineAddress(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="USA"
                  className="bg-white border-amber-300"
                />
              </div>
            </div>
            
            <div className="pt-4 border-t border-amber-300/50">
              <p className="text-amber-800/60 text-xs italic text-center">
                "In a world of screens, paper rebels"
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowZineDialog(false)}
              className="flex-1 border-amber-800/30 text-amber-900"
            >
              Maybe Later
            </Button>
            <Button 
              onClick={() => {
                setRequestZine(true);
                setShowZineDialog(false);
                toast.success('Zine request noted! Complete your quest to submit.');
              }}
              className="flex-1 bg-amber-800 hover:bg-amber-900 text-white"
            >
              Request Zine
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Golden Summary Dialog - Vision Quest Summary */}
      <GoldenSummary
        isOpen={showGoldenSummary}
        onClose={() => setShowGoldenSummary(false)}
        belongingData={null}
        visionData={{
          title: visionTitle,
          values: selectedValues,
          purpose: purpose,
          bhag: bhag,
          story: generatedStory
        }}
        includeVision={true}
        userId={userId}
      />
      
      {/* Soul Score Widget for Vision Quest checkpoints */}
      <SoulScoreWidget
        isOpen={showSoulScore}
        onClose={() => setShowSoulScore(false)}
        checkpoint={soulScoreCheckpoint}
        userId={userId}
        sessionId={sessionId}
        onComplete={(result) => {
          setShowSoulScore(false);
          setSoulScoreCompleted(prev => ({
            ...prev,
            [result.checkpoint]: true
          }));
          // Continue navigation after Soul Score
          if (result.checkpoint === 'vision') {
            setPhase(PHASES.review);
          }
        }}
        onSkip={() => {
          setShowSoulScore(false);
          // Also continue navigation on skip
          if (soulScoreCheckpoint === 'vision') {
            setPhase(PHASES.review);
          }
        }}
      />
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default VisionQuest;
